import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Mongo } from 'meteor/mongo';
import { strings } from '/imports/strings';
import { objectValues, randomString, referenceString } from '/imports/utils';
import { MethodDeclaration } from '/imports/methods';
import { Dict, Docmnt, ListMethodArg, ListMethodResponse, ModelClass } from '/imports/types';
import { Icon } from '/imports/ui/Icon';
import { Input } from '/imports/ui/Input';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { isIos } from '/imports/ios';

// Tailwind port of the former RichSelectStyled styled.div.
// The non-iOS mobile fullscreen-on-open block is only emitted when !isIos(),
// matching the old conditional template. `.open`/`.has-value`/`.error` remain
// literal classes toggled in the JSX; the styling reaches them via arbitrary
// `[&.open ...]` variants. (css.primaryColorTransparent === rgba(28,61,147,0.5).)
// This isIos check might need to include mobile safari too...use useragent
const mobileOpenClasses = !isIos() ?
  'mobile:[&.open]:fixed mobile:[&.open]:top-4 mobile:[&.open]:right-4 mobile:[&.open]:w-[calc(100%-2rem)] mobile:[&.open]:h-[calc(100%-3rem)] mobile:[&.open]:z-[3] '
    + 'mobile:[&.open_.mobile-underlay]:block mobile:[&.open_.mobile-underlay]:fixed mobile:[&.open_.mobile-underlay]:top-0 mobile:[&.open_.mobile-underlay]:left-0 mobile:[&.open_.mobile-underlay]:w-full mobile:[&.open_.mobile-underlay]:h-full mobile:[&.open_.mobile-underlay]:bg-[rgba(28,61,147,0.5)] mobile:[&.open_.mobile-underlay]:z-[3] '
    + 'mobile:[&.open_.input]:z-[4] mobile:[&.open_.options]:z-[4]'
  :
  '';

const richSelectClasses = [
  mobileOpenClasses,
  'relative w-full',
  // &.has-value input::placeholder
  '[&.has-value_input::placeholder]:text-grey-2 [&.has-value_input::placeholder]:not-italic',
  // input { width: 100% }
  '[&_input]:w-full',
  // &.error input { border: 1px solid red-2 }
  '[&.error_input]:border [&.error_input]:border-red-2',
  // .mobile-underlay { display: none }
  '[&_.mobile-underlay]:hidden',
].filter(Boolean).join(' ');

export interface Option {
  data?: any,
  value: string,
  label: string,
  render?: (option: Option) => React.ReactNode,
  clear?: boolean,
}

export interface RichSelectProps<T extends Docmnt = any, S extends Docmnt = any> {
  id?: string;
  className?: string;
  placeholder?: string;
  options?: Option[]
  listMethod?: MethodDeclaration<ListMethodArg<T>, ListMethodResponse<T>>;
  listQuery?: Mongo.Query<T> | (() => Mongo.Query<T>);
  mapToValue?(obj: T): Option;
  loading?: boolean;
  value?: string;
  preSelect?: {
    modelClass: ModelClass<T>;
    document: S;
    field: keyof T;
  };
  preSelectedOption?: Option;
  disabled?: boolean;
  defaultOpen?: boolean;
  optionsHeader?: React.ReactNode;
  optionsFooter?: React.ReactNode;
  clearable?: boolean;
  filter?(option: Option): boolean;
  onChange?(option: Option): void;
  onClear?(): void;
  onSearch?(search?: string, e?: React.ChangeEvent<HTMLInputElement>): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  selectionTrigger?(handler: (option: Option, triggerOnChange?: boolean) => void): void;
  fixedOptions?: boolean;
}

let handlingScrollBottom = false;

const _document = document;

export const RichSelect = <T extends Docmnt = any>(props: RichSelectProps<T>) => {
  const { value, options, listMethod, mapToValue, loading, disabled, preSelect, preSelectedOption, optionsHeader, optionsFooter,
    filter, listQuery, clearable, fixedOptions } = props;
  const { modelClass, document, field } = preSelect || {};
  const defaultOption = modelClass && field && document?.[field] ?
    { value: document[field] as string, label: referenceString(modelClass, document, field as string) }
    :
    preSelectedOption;
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(defaultOption);
  const [stateOptions, setStateOptions] = useState<Option[]>(options || (defaultOption && [defaultOption]) || []);
  const [listLoading, setListLoading] = useState(!!listMethod);
  const [search, setSearch] = useState<string>();
  const [open, setOpen] = useState(!!props.defaultOpen);
  const _listQuery = typeof listQuery === 'function' ? listQuery() : listQuery;
  const [methodArg, setMethodArg] = useState<ListMethodArg<T>>({ skip: 0, limit: 20, query: _listQuery, other: { noMeta: true } });
  const [focusedOption,] = useState<any>();
  const [inputId,] = useState(randomString());
  const [optionsId,] = useState(randomString());
  const [scrollExhausted, setScrollExhausted] = useState(false);
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (focusedOption) {
        // ToDo;
      }
    }
  };
  const selectionTrigger = (option: Option) => {
    setSelectedOption(option);
  };
  useEffect(() => {
    props.selectionTrigger?.(selectionTrigger);
  }, []);
  useEffect(() => {
    if (!options) return;
    if (listLoading || !listMethod) {
      setStateOptions(mergeOptions([...stateOptions, ...options]));
      return;
    }
    if (listMethod && !listLoading) return;
  }, [options]);
  const mapOptions = (documents: T[]) => {
    return documents.map((obj) => mapToValue ?
      mapToValue(obj)
      :
      { value: obj._id, label: (obj as any)._name || modelClass?.asString?.(obj) || (obj as any).name, data: obj } as Option);
  };
  const mergeOptions = (options: Option[]) => {
    const _options = objectValues([...options].reduce((map: Dict<Option>, opt) => {
      map[opt.value] = opt;
      return map;
    }, {}));
    return _options;
  };
  useEffect(() => {
    if (!listMethod) return;
    (async () => {
      try {
        handlingScrollBottom = true;
        setListLoading(true);
        const response = await listMethod.call(methodArg);
        const options = mapOptions(response.documents);
        const newOptions = mergeOptions([...stateOptions, ...options]);
        setStateOptions(newOptions);
        !options.length && setScrollExhausted(true);
      }
      catch (error) {
        toast.error(`${error}`);
      }
      finally {
        handlingScrollBottom = false;
        setListLoading(false);
      }
    })();
  }, [listMethod, methodArg]);
  useEffect(() => {
    if (!listMethod || !value) return;
    (async () => {
      const response = await listMethod.call({ query: { _id: value as any }, limit: 1, other: { noMeta: true } });
      const option = mapOptions(response.documents)[0];
      if (!option) return; // lookup found nothing; don't crash mergeOptions
      const newOptions = mergeOptions([...stateOptions, option]);
      setStateOptions(newOptions);
      setSelectedOption({ ...option });
    })();
  }, [value]);
  useEffect(() => {
    if (!listMethod) return;
    // Debounced: one list call per pause in typing, not one per keystroke.
    const timer = setTimeout(() => {
      setScrollExhausted(false);
      setStateOptions(defaultOption && [defaultOption] || []);
      setMethodArg({ ...methodArg, search, skip: 0, query: _listQuery });
    }, search === undefined ? 0 : 300);
    return () => clearTimeout(timer);
  }, [search, JSON.stringify(_listQuery || {})]);
  useEffect(() => {
    setSelectedOption(stateOptions?.filter((o) => o.value === value)[0] || selectedOption);
  }, [stateOptions]);
  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (scrollExhausted) return;
    const target = e.target as HTMLElement;
    const hitBottom = target.scrollHeight - target.scrollTop - 0.1 * target.scrollHeight <= target.clientHeight;
    if (hitBottom && !handlingScrollBottom) {
      handlingScrollBottom = true;
      setMethodArg({ ...methodArg, skip: stateOptions.length, query: _listQuery });
    }
  };
  const setOptionsHeight = () => {
    const input = _document.getElementById(inputId);
    const options = _document.getElementById(optionsId);
    const inputRect = input?.getBoundingClientRect();
    const optionsRect = options?.getBoundingClientRect();
    const top = inputRect?.bottom;
    if (options && top) options.style.maxHeight = `${window.innerHeight - top - 16}px`;
    if (open && options && fixedOptions) {
      options.style.position = 'fixed';
      options.style.top = `${optionsRect?.top}px`;
      options.style.left = `${optionsRect?.left}px`;
      options.style.width = `${optionsRect?.width}px`;
    }
    else if (options && fixedOptions) {
      options.style.removeProperty('position');
      options.style.removeProperty('top');
      options.style.removeProperty('left');
      options.style.removeProperty('width');
    }
  };
  useEffect(() => {
    setOptionsHeight();
    const handler = () => setOptionsHeight();
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('resize', handler); };
  }, [open]);
  const searchFilter = (option: Option) => {
    const _search = search?.trim().toLowerCase();
    if (!_search) return true;
    return option.label.toLowerCase().includes(_search);
  };
  return (
    <div id={props.id} className={`${richSelectClasses} ${props.className}${open ? ' open' : ''}${selectedOption ? ' has-value' : ''}`} key={selectedOption?.value}>
      <div className="mobile-underlay" />
      <Input
        id={inputId}
        className="rich-select-search [&_.clear]:hidden"
        placeholder={selectedOption?.label || props.placeholder || strings.Search()}
        value={search}
        disabled={disabled}
        onChange={(search, e) => { setSearch(search); props.onSearch?.(search, e); }}
        onFocus={(e) => { setOpen(true); props.onFocus?.(e); }}
        onBlur={(e) => { setOpen(false); setSearch(''); props.onBlur?.(e); }}
        onKeyPress={(e) => handleKeyPress(e)}
      />
      {clearable !== false && <Icon className="clear absolute top-3 right-3" icon="cancel" onClick={() => { setSelectedOption(undefined); props.onClear?.(); }} /> || null}
      <div
        className={`options absolute w-full overflow-auto rounded-input border border-mid bg-panel text-grey-2 [&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar-track]:bg-[red] [&::-webkit-scrollbar-thumb]:bg-[red] [&::-webkit-scrollbar-thumb:hover]:bg-[red] ${open ? 'open z-[1] block' : 'z-[1] hidden'}`}
        id={optionsId}
        onScroll={handleScroll}
      >
        <div className="rich-select-options-header cursor-pointer p-2 font-bold text-primary">{optionsHeader || null}</div>
        {
          stateOptions?.filter(searchFilter).filter(filter || Boolean).map((option) => {
            return (
              <div
                key={option.value}
                className="option cursor-pointer px-2 py-4 hover:text-primary"
                onMouseDown={() => { setSelectedOption(option); setOpen(false); option.clear ? props.onClear?.() : props.onChange?.(option); }}
              >
                {option.render?.(option) || option.label}
              </div>
            );
          })
        }
        {
          open && !loading && !listLoading && !stateOptions?.length ?
            (
              <div className="no-results flex min-h-input flex-col justify-around text-center capitalize">{strings.NoResults()}</div>
            )
            :
            null
        }
        {
          loading || listLoading ?
            (
              <div className="loading h-16">
                <LoadingFill />
              </div>
            )
            :
            null
        }
        <div className="rich-select-options-footer cursor-pointer p-2 font-bold text-primary">{optionsFooter || null}</div>
      </div>
    </div>
  );
};